import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Zap, ChevronDown, ChevronUp, Info, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { UnidadeConsumidora, Negociacao } from '../../../../types';
import { logNegotiationAction } from '../../../../src/lib/negotiationLogger';
import { logUsageEvent } from '../../../../src/lib/logger';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { checkAndRequestDiscountApproval } from '../../../../src/lib/discountApproval';
import PlanoParceiroFields from '../../../../src/components/PlanoParceiroFields';
import PlanoComercialInternoFields from '../../../../src/components/PlanoComercialInternoFields';
import CondicaoEspecialFields from '../../../../src/components/CondicaoEspecialFields';
import {
    PUBLICOS,
    fidelidadeOpcoesDoPlano,
    getFatorFidelidade,
    carenciaOpcoesCom,
    isCarenciaSolicitada,
    isFidelidadeIsenta,
    classificarPublico,
    calcularMultiplicadorFinal,
    inferirPlanoHistorico,
    corrigirPlanoPorPercentuais,
    getPlano,
    planosSelecionaveis,
    resolverModalidadePlano,
    PlanoComercial,
} from '../../../../src/lib/politicaComercial';

interface PlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    ucs: UnidadeConsumidora[];
    negotiation: Negociacao;
    onSave: () => void;
}

const fmtKwh = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`;
const fmtMult = (v: number) => `${v.toFixed(2).replace('.', ',')}x`;

const PlanModal: React.FC<PlanModalProps> = ({ isOpen, onClose, ucs, negotiation, onSave }) => {
    const { user, profile } = useAuth();
    // Modalidade é da NEGOCIAÇÃO (carimbada na criação), não de quem está olhando:
    // gerente que abre uma negociação de parceiro vê e grava o mesmo modelo simples
    // de percentuais que o parceiro criou — sem plano_codigo nem multiplicador.
    const modalidade = resolverModalidadePlano(negotiation, profile);
    const isParceiro = modalidade === 'parceiro';
    const isVendedorInterno = modalidade === 'vendedor_interno';
    const { addToast } = useToast();
    const [planoCodigo, setPlanoCodigo] = useState<string>('');
    const [fidelidade, setFidelidade] = useState('Sem fidelidade');
    const [descontoAdicional, setDescontoAdicional] = useState('');
    const [manualTarifa, setManualTarifa] = useState(0);
    const [manualBandeira, setManualBandeira] = useState(0);
    const [carencia, setCarencia] = useState('');
    const [showUcList, setShowUcList] = useState(false);
    const [loading, setLoading] = useState(false);
    const prevIsOpenRef = useRef(false);

    // Volume total da negociação: soma do consumo cadastrado nas UCs (somente leitura aqui)
    const volumeTotal = useMemo(
        () => ucs.reduce((s, u) => s + (Number(u.consumo_medio_kwh) || 0), 0),
        [ucs]
    );
    const publico = useMemo(() => classificarPublico(volumeTotal), [volumeTotal]);

    useEffect(() => {
        const justOpened = isOpen && !prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;
        if (!isOpen || !justOpened) return;

        const tarifaPct = Math.round((ucs[0]?.desconto_tarifa || 0) * 100);
        const bandeiraPct = Math.round((ucs[0]?.desconto_bandeira || 0) * 100);
        const planoSalvo = getPlano(negotiation.plano_codigo);
        // Inferência histórica: negociação sem plano gravado é anterior à virada,
        // então só pode cair em plano que já existia quando ela foi vendida. Sem
        // isso, uma negociação antiga em 20%/50% abriria com o 20_50_bandeira já
        // marcado e um save qualquer carimbaria a política nova num contrato velho.
        const planoInferido = inferirPlanoHistorico(tarifaPct, bandeiraPct);

        // "Fora da política" gravado com percentuais de plano oficial era erro de
        // preenchimento: abre já no plano certo. Só para esse caso — negociação
        // sem plano segue a inferência histórica acima.
        const codigoSalvo = planoSalvo?.foraDaPolitica
            ? corrigirPlanoPorPercentuais(planoSalvo.codigo, tarifaPct, bandeiraPct)
            : planoSalvo?.codigo;
        setPlanoCodigo(codigoSalvo || planoInferido?.codigo || '');
        // Sem fidelidade gravada abre em 12 meses: "Isento" agora pede aprovação (6532).
        setFidelidade(negotiation.fidelidade || '12 meses');
        setDescontoAdicional(negotiation.desconto_adicional || '');
        setManualTarifa(tarifaPct);
        setManualBandeira(bandeiraPct);
        setCarencia(ucs[0]?.carencia || 'sem carência');
        setShowUcList(false);
    }, [ucs, negotiation, isOpen]);

    const planoSelecionado = getPlano(planoCodigo);
    const isForaDaPolitica = planoSelecionado?.foraDaPolitica ?? false;

    const resultado = useMemo(() => {
        if (!planoCodigo) return null;
        return calcularMultiplicadorFinal(planoCodigo, volumeTotal, fidelidade);
    }, [planoCodigo, volumeTotal, fidelidade]);

    // Plano descontinuado só entra na lista se for o que o vendedor fechou — e
    // aí sai do bloco principal, com aviso, para não parecer uma opção corrente.
    // Ao trocar para um plano atual ele some e não há como voltar.
    const planosVisiveis = useMemo(() => planosSelecionaveis(planoCodigo), [planoCodigo]);
    const planosAtivos = useMemo(() => planosVisiveis.filter(p => !p.legado && !p.foraDaPolitica), [planosVisiveis]);
    const planoLegado = useMemo(() => planosVisiveis.find(p => p.legado) ?? null, [planosVisiveis]);

    const renderPlanoBotao = (plano: PlanoComercial) => {
        const selected = planoCodigo === plano.codigo;
        return (
            <button
                key={plano.codigo}
                type="button"
                onClick={() => setPlanoCodigo(plano.codigo)}
                className={`w-full text-left rounded-xl border px-4 py-2.5 transition-all flex items-center justify-between gap-2 ${
                    plano.legado
                        ? selected
                            ? 'border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/20 ring-2 ring-red-200 dark:ring-red-900/40'
                            : 'border-red-200 dark:border-red-900/50 bg-red-50/40 dark:bg-red-900/10 hover:border-red-400'
                        : selected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-gray-200 dark:border-slate-700 hover:border-primary/40 bg-white dark:bg-slate-900'
                } ${plano.foraDaPolitica ? 'border-dashed' : ''}`}
            >
                <div className="min-w-0">
                    <p className={`text-sm font-semibold ${
                        plano.legado ? 'text-red-700 dark:text-red-400'
                            : plano.foraDaPolitica ? 'text-orange-600 dark:text-orange-400'
                                : 'text-gray-800 dark:text-slate-100'
                    }`}>
                        {plano.label}
                    </p>
                    {!plano.foraDaPolitica && (
                        <p className={`text-[11px] ${plano.legado ? 'text-red-500/80 dark:text-red-400/70' : 'text-gray-500 dark:text-slate-400'}`}>
                            {plano.descTarifa}% tarifa · {plano.descBandeira}% bandeira
                        </p>
                    )}
                    {plano.foraDaPolitica && (
                        <p className="text-[11px] text-orange-500/80">Exige aprovação do gerente</p>
                    )}
                </div>
                <span className={`shrink-0 text-sm font-bold font-mono px-2.5 py-1 rounded-lg ${
                    plano.legado
                        ? selected
                            ? 'bg-red-600 text-white'
                            : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
                        : selected
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300'
                }`}>
                    {fmtMult(plano.mult[publico])}
                </span>
            </button>
        );
    };

    // Fatores resolvidos pelo plano selecionado — no 20/50 os três prazos valem
    // 1,00x, nos demais a escala por prazo continua valendo.
    // Opção legada '18 meses' continua visível apenas se já for o valor gravado.
    const fidelidadeOpcoes = useMemo(() => {
        const opcoes = fidelidadeOpcoesDoPlano(planoCodigo);
        if (fidelidade === '18 meses') {
            opcoes.splice(2, 0, { valor: '18 meses', label: '18 meses (legado)', fator: getFatorFidelidade('18 meses', planoCodigo) });
        }
        return opcoes;
    }, [fidelidade, planoCodigo]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (ucs.length === 0) {
            addToast('Não há UCs para aplicar o plano.', 'error');
            onClose();
            return;
        }
        if (!isParceiro && !isVendedorInterno && (!planoCodigo || !planoSelecionado || !resultado)) {
            addToast('Selecione um plano da política comercial.', 'error');
            return;
        }

        // Parceiro e Comercial Interno editam os percentuais direto (manualTarifa/
        // manualBandeira) — não existe "plano" empacotado pra nenhum dos dois.
        const descTarifa = (isParceiro || isVendedorInterno) ? manualTarifa : (isForaDaPolitica ? manualTarifa : planoSelecionado!.descTarifa);
        const descBandeira = (isParceiro || isVendedorInterno) ? manualBandeira : (isForaDaPolitica ? manualBandeira : planoSelecionado!.descBandeira);

        // Contrato já assinado/ganho: mudar o desconto de bandeira aqui reescreve
        // todas as UCs e vai direto para a emissão de boleto e o cálculo da fatura.
        // Isso é alteração de valor faturado — o caminho formal é o aditivo.
        const bandeiraAtual = Math.round((ucs[0]?.desconto_bandeira || 0) * 100);
        const contratoVigente = ['termo_assinado', 'termo_aprovado', 'ganho'].includes(negotiation.status);
        if (contratoVigente && descBandeira !== bandeiraAtual) {
            const confirmado = window.confirm(
                `Esta negociação já está com contrato vigente e o desconto na bandeira mudaria de ${bandeiraAtual}% para ${descBandeira}% em todas as ${ucs.length} UC(s).\n\n` +
                'Isso altera o valor faturado do cliente e afeta os próximos boletos. O caminho formal para essa mudança é o aditivo contratual.\n\n' +
                'Deseja aplicar mesmo assim?'
            );
            if (!confirmado) return;
        }

        setLoading(true);
        try {
            const ucIds = ucs.map(uc => uc.id);

            const updateUCs = supabase
                .from('unidades_consumidoras')
                .update({
                    desconto_tarifa: descTarifa / 100,
                    desconto_bandeira: descBandeira / 100,
                    carencia: carencia
                })
                .in('id', ucIds);

            // Comercial Interno não grava plano_codigo/publico/multiplicador_* —
            // esses campos são do vendedor externo. O motor de fechamento
            // (comissaoInternaV1.ts) recalcula tudo na hora do fechamento a partir
            // de desconto_tarifa/desconto_bandeira/fidelidade/origem, não lê snapshot
            // daqui. Não tem legado pra proteger (modalidade nova), então recalcular
            // ao vivo é mais simples que espelhar o snapshot do vendedor externo.
            const updateNegotiation = supabase
                .from('negociacoes')
                .update({
                    fidelidade: fidelidade,
                    desconto_adicional: descontoAdicional,
                    consumo_medio_kwh: volumeTotal,
                    // Auto-cura de negociações anteriores à coluna (resolvidas pelo cargo/departamento do responsável)
                    plano_modalidade: modalidade,
                    plano_codigo: (isParceiro || isVendedorInterno) ? null : planoCodigo,
                    publico: (isParceiro || isVendedorInterno) ? null : resultado!.publico,
                    multiplicador_plano: (isParceiro || isVendedorInterno) ? null : resultado!.multPlano,
                    fator_fidelidade: (isParceiro || isVendedorInterno) ? null : resultado!.fatorFidelidade,
                    multiplicador_final: (isParceiro || isVendedorInterno) ? null : resultado!.final
                })
                .eq('id', negotiation.id);

            const [resUC, resNeg] = await Promise.all([updateUCs, updateNegotiation]);

            if (resUC.error) throw resUC.error;
            if (resNeg.error) throw resNeg.error;

            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'alterar_plano_negociacao', origem: 'acao' });
            await logNegotiationAction(negotiation.id, user?.id, 'alterou o plano', {
                plano: isParceiro ? 'Parceiro (sem multiplicador)' : isVendedorInterno ? 'Closer e BDR (desconto direto)' : planoSelecionado!.label,
                desconto_tarifa: descTarifa,
                desconto_bandeira: descBandeira,
                fidelidade: fidelidade,
                publico: (isParceiro || isVendedorInterno) ? null : resultado!.publico,
                multiplicador_final: (isParceiro || isVendedorInterno) ? null : resultado!.final,
                desconto_adicional: descontoAdicional
            });

            // --- APPROVAL LOGIC (dentro/fora da política + carência) ---
            await checkAndRequestDiscountApproval(
                negotiation.id,
                user?.id,
                descTarifa,
                descBandeira,
                fidelidade,
                {
                    planoCodigo: isParceiro ? null : planoCodigo,
                    carencia,
                    // Só quem troca PARA isento pede aprovação; quem já era isento segue como está.
                    fidelidadeIsenta: isFidelidadeIsenta(fidelidade) && !isFidelidadeIsenta(negotiation.fidelidade),
                }
            );

            addToast('Plano atualizado com sucesso!', 'success');
            onSave();
            onClose();
        } catch (error: any) {
            console.error('Error saving plan:', error);
            addToast('Erro ao salvar plano: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4 ${!isOpen ? 'hidden' : ''}`}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden transform scale-100 transition-all max-h-[92vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700/50 flex justify-between items-center bg-gray-50 dark:bg-slate-800/50 shrink-0">
                    <h3 className="font-bold text-gray-800 dark:text-slate-100 text-lg">
                        Alterar Plano
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full transition-colors text-gray-500 dark:text-slate-400"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden flex-1">
                    <div className="p-6 space-y-5 overflow-y-auto flex-1">

                        <div className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-500 text-xs p-3 rounded-lg border border-yellow-100 dark:border-yellow-900/50">
                            Atenção: As alterações aplicadas aqui afetarão <strong>todas</strong> as {ucs.length} unidades consumidoras desta negociação.
                        </div>

                        {/* 1. Volume total + público */}
                        <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-4 bg-gray-50 dark:bg-slate-800/60 flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                                        <Zap size={20} />
                                    </div>
                                    <div>
                                        <p className="text-[11px] font-bold uppercase text-gray-500 dark:text-slate-400">Volume total da negociação</p>
                                        <p className="text-xl font-bold text-gray-800 dark:text-slate-100">{fmtKwh(volumeTotal)}</p>
                                    </div>
                                </div>
                                {!isParceiro && !isVendedorInterno && (
                                    <div className="text-right">
                                        <p className="text-[11px] font-bold uppercase text-gray-500 dark:text-slate-400 mb-1">Público identificado</p>
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${
                                            publico === 'A' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
                                            : publico === 'B' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400'
                                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                                        }`}>
                                            {PUBLICOS[publico].label}
                                            <span className="font-normal text-xs opacity-80">· {PUBLICOS[publico].faixaLabel}</span>
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="px-4 py-2.5 border-t border-gray-100 dark:border-slate-700/50 flex items-center justify-between text-xs text-gray-500 dark:text-slate-400">
                                <span className="flex items-center gap-1.5">
                                    <Info size={13} className="shrink-0" />
                                    Volume somado do consumo cadastrado nas UCs. Para alterar, edite a UC na negociação — não neste modal.
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowUcList(v => !v)}
                                    className="flex items-center gap-1 font-semibold text-primary hover:underline shrink-0"
                                >
                                    {showUcList ? 'Ocultar UCs' : `Ver ${ucs.length} UC${ucs.length !== 1 ? 's' : ''}`}
                                    {showUcList ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                            </div>
                            {showUcList && (
                                <div className="border-t border-gray-100 dark:border-slate-700/50 divide-y divide-gray-50 dark:divide-slate-800 max-h-40 overflow-y-auto">
                                    {ucs.map(uc => (
                                        <div key={uc.id} className="px-4 py-2 flex justify-between text-xs">
                                            <span className="text-gray-600 dark:text-slate-300 font-medium">UC {uc.numero_uc}</span>
                                            <span className="text-gray-800 dark:text-slate-100 font-semibold">{fmtKwh(Number(uc.consumo_medio_kwh) || 0)}</span>
                                        </div>
                                    ))}
                                    <div className="px-4 py-2 flex justify-between text-xs bg-gray-50 dark:bg-slate-800/60">
                                        <span className="font-bold text-gray-700 dark:text-slate-200">Total</span>
                                        <span className="font-bold text-gray-900 dark:text-white">{fmtKwh(volumeTotal)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {isParceiro ? (
                            <PlanoParceiroFields
                                descTarifa={manualTarifa}
                                descBandeira={manualBandeira}
                                onChangeDescTarifa={setManualTarifa}
                                onChangeDescBandeira={setManualBandeira}
                                volumeKwh={volumeTotal}
                                fidelidade={fidelidade}
                                onChangeFidelidade={setFidelidade}
                                carencia={carencia}
                                onChangeCarencia={setCarencia}
                            />
                        ) : isVendedorInterno ? (
                            <PlanoComercialInternoFields
                                descTarifa={manualTarifa}
                                descBandeira={manualBandeira}
                                onChangeDescTarifa={setManualTarifa}
                                onChangeDescBandeira={setManualBandeira}
                                fidelidade={fidelidade}
                                onChangeFidelidade={setFidelidade}
                                carencia={carencia}
                                onChangeCarencia={setCarencia}
                            />
                        ) : (
                        <div className="grid lg:grid-cols-2 gap-5">
                            {/* 2. Seleção de plano */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                                        Plano da política comercial
                                    </label>
                                    <div className="space-y-2">
                                        {planosAtivos.map(renderPlanoBotao)}
                                    </div>

                                    {planoLegado && (
                                        <div className="mt-4 pt-3 border-t-2 border-dashed border-red-200 dark:border-red-900/50">
                                            <p className="flex items-start gap-1.5 text-[11px] font-bold text-red-600 dark:text-red-400 mb-2">
                                                <AlertTriangle size={13} className="shrink-0 mt-px" />
                                                <span>
                                                    Plano descontinuado — condição da política antiga, mantida aqui porque foi
                                                    a que o vendedor fechou. Não vale para vendas novas. Ao trocar por um plano
                                                    atual esta opção desaparece e não há como voltar para ela.
                                                </span>
                                            </p>
                                            {renderPlanoBotao(planoLegado)}
                                        </div>
                                    )}
                                </div>

                                <CondicaoEspecialFields
                                    ativo={isForaDaPolitica}
                                    descTarifa={manualTarifa}
                                    descBandeira={manualBandeira}
                                    onChangeDescTarifa={setManualTarifa}
                                    onChangeDescBandeira={setManualBandeira}
                                    volumeKwh={volumeTotal}
                                    fidelidade={fidelidade}
                                    onAtivar={(t, b) => {
                                        setPlanoCodigo('fora_da_politica');
                                        setManualTarifa(t);
                                        setManualBandeira(b);
                                    }}
                                    onCancelar={() => setPlanoCodigo('22_50_bandeira')}
                                />

                                {/* 3. Fidelidade */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                                        Fidelidade <span className="font-normal normal-case text-gray-400 dark:text-slate-500">(fator de ajuste)</span>
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {fidelidadeOpcoes.map(o => {
                                            const selected = fidelidade === o.valor;
                                            return (
                                                <button
                                                    key={o.valor}
                                                    type="button"
                                                    onClick={() => setFidelidade(o.valor)}
                                                    className={`rounded-xl border px-3 py-2 text-left transition-all flex items-center justify-between gap-2 ${
                                                        selected
                                                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                                            : 'border-gray-200 dark:border-slate-700 hover:border-primary/40 bg-white dark:bg-slate-900'
                                                    }`}
                                                >
                                                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-200">{o.label}</span>
                                                    <span className="text-[11px] font-mono font-bold text-gray-500 dark:text-slate-400">{fmtMult(o.fator)}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Carência para solicitações — padrão: sem carência; com carência exige aprovação */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                                        Carência <span className="font-normal normal-case text-gray-400 dark:text-slate-500">(para solicitações)</span>
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {carenciaOpcoesCom(carencia).map(o => {
                                            const selected = carencia === o.valor || (o.valor === 'sem carência' && !isCarenciaSolicitada(carencia));
                                            return (
                                                <button
                                                    key={o.valor}
                                                    type="button"
                                                    onClick={() => setCarencia(o.valor)}
                                                    className={`rounded-xl border px-3 py-2 text-left transition-all ${
                                                        selected
                                                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                                            : 'border-gray-200 dark:border-slate-700 hover:border-primary/40 bg-white dark:bg-slate-900'
                                                    }`}
                                                >
                                                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-200">{o.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {isCarenciaSolicitada(carencia) && (
                                        <p className="flex items-center gap-1.5 text-[11px] text-orange-600 dark:text-orange-400 mt-2">
                                            <AlertTriangle size={12} className="shrink-0" />
                                            Carência exige aprovação do gerente ou admin — a solicitação será enviada ao salvar.
                                        </p>
                                    )}
                                </div>

                                {(profile?.role === 'adm' || profile?.role === 'gerente') && (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                                            Desconto adicional
                                        </label>
                                        <input
                                            type="text"
                                            value={descontoAdicional}
                                            onChange={e => setDescontoAdicional(e.target.value)}
                                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                                            placeholder="Ex: Condição promocional no primeiro mês..."
                                        />
                                    </div>
                                )}
                            </div>

                            {/* 4. Tabela de multiplicadores */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                                    Multiplicadores por público
                                </label>
                                <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-gray-50 dark:bg-slate-800/60 text-gray-500 dark:text-slate-400">
                                                <th className="text-left px-3 py-2 font-bold uppercase text-[10px]">Plano</th>
                                                {(['C', 'B', 'A'] as const).map(p => (
                                                    <th key={p} className={`px-2 py-2 font-bold uppercase text-[10px] text-center ${p === publico ? 'bg-primary/10 text-primary' : ''}`}>
                                                        Púb. {p}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                                            {/* A linha "fora da política" só aparece na referência quando a
                                                condição especial já está escolhida — fora disso ela não é oferta. */}
                                            {planosVisiveis.filter(p => !p.foraDaPolitica || isForaDaPolitica).map(plano => {
                                                const isRow = plano.codigo === planoCodigo;
                                                return (
                                                    <tr
                                                        key={plano.codigo}
                                                        className={`${plano.legado ? 'border-t-2 border-dashed border-red-200 dark:border-red-900/50 bg-red-50/40 dark:bg-red-900/10' : isRow ? 'bg-primary/5' : ''}`}
                                                    >
                                                        <td className={`px-3 py-2 ${
                                                            plano.legado ? 'font-bold text-red-700 dark:text-red-400'
                                                                : isRow ? 'font-bold text-primary'
                                                                    : 'text-gray-600 dark:text-slate-300'
                                                        }`}>
                                                            {plano.label}
                                                            {plano.legado && (
                                                                <span className="block text-[10px] font-normal text-red-500/80">
                                                                    Descontinuado — não vale para vendas novas
                                                                </span>
                                                            )}
                                                        </td>
                                                        {(['C', 'B', 'A'] as const).map(p => (
                                                            <td key={p} className={`px-2 py-2 text-center font-mono ${
                                                                p === publico && isRow
                                                                    ? 'bg-primary text-white font-bold rounded'
                                                                    : p === publico
                                                                        ? 'bg-primary/10 text-primary font-semibold'
                                                                        : isRow
                                                                            ? 'font-semibold text-gray-700 dark:text-slate-200'
                                                                            : 'text-gray-500 dark:text-slate-400'
                                                            }`}>
                                                                {fmtMult(plano.mult[p])}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-2">
                                    Coluna destacada = público da negociação · linha destacada = plano selecionado.
                                </p>
                            </div>
                        </div>
                        )}
                    </div>

                    {/* Footer: multiplicador final + ações */}
                    <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/50 flex flex-wrap items-center justify-between gap-3 shrink-0">
                        <div>
                            {!isParceiro && !isVendedorInterno && (
                                <>
                                    <p className="text-[11px] font-bold uppercase text-gray-500 dark:text-slate-400">Multiplicador final da negociação</p>
                                    {resultado ? (
                                        <p className="text-gray-800 dark:text-slate-100">
                                            <span className="text-2xl font-bold text-primary">{fmtMult(resultado.final)}</span>
                                            <span className="text-xs text-gray-500 dark:text-slate-400 ml-2">
                                                {fmtMult(resultado.multPlano)} (plano) × {fmtMult(resultado.fatorFidelidade)} (fidelidade)
                                            </span>
                                        </p>
                                    ) : (
                                        <p className="text-sm text-gray-400 dark:text-slate-500">Selecione um plano para calcular</p>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading || ucs.length === 0 || (!isParceiro && !isVendedorInterno && !planoCodigo)}
                                className="px-6 py-2.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/30 flex items-center gap-2 disabled:opacity-70"
                            >
                                <Save size={18} />
                                {loading ? 'Salvando...' : 'Salvar Alterações'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default PlanModal;
